import React, { useState, useEffect, useRef, useCallback, Fragment } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { dashboardToast } from "../../utils/dashboardToast";
import "bootstrap/dist/css/bootstrap.min.css";

import Menu from "../components/menu";
import DashboardShell from "../../components/DashboardShell";
import { useMenuState } from "../../Common/MenuStateContext";
import SaListPageShell from "../../components/sa/SaListPageShell";
import ContentLoader from "../../components/ContentLoader";
import RecordDetailModal from "../../components/RecordDetailModal";
import TableSortSelect from "../../components/TableSortSelect";
import { Alert, Button as UiButton, Pagination as UiPagination } from "../../components/ui";
import { TABLE_SORT } from "../../utils/tableSort";
import { getBusinessFunctions, addOrUpdateBusinessFunction, deleteBusinessFunction } from "../../services/CostEstimationsetupApi";
import { ChevronDown, ChevronRight, Layers, Pencil, Plus, Search, Settings, Trash2 } from "lucide-react";
import "../../assets/css/dashboard.css";

const EmptyCell = () => <span className="app-td__empty">—</span>;

const FunctionsManagement = () => {
  const { isMenuCollapsed, toggleMenuCollapse } = useMenuState();
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);
  const [viewMode, setViewMode] = useState("parent"); // "parent" | "child"
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(""); // "businessFunction" or "subBusinessFunction"
  const [expandedRows, setExpandedRows] = useState(new Set()); // Track expanded business function rows

  // Business Functions form data
  const [businessFunctionForm, setBusinessFunctionForm] = useState({
    id: 0,
    name: "",
    code: "",
    isActive: true,
    subFunctions: [] // New state for sub-functions
  });
  // Business functions data from API (includes subBusinessFunctions)
  const [businessFunctions, setBusinessFunctions] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState(TABLE_SORT.DATE_DESC);
  const dataFetchedRef = useRef(false);

  const handleMenuSelection = useCallback((item) => {
    setSelectedItem(item);
    return item;
  }, []);

  const [apiPageInfo, setApiPageInfo] = useState({
    pageNumber: 1,
    pageSize: 100,
    totalPages: 1,
    totalCount: 0,
  });

  // Fetch business functions from API
  const fetchBusinessFunctions = useCallback(async (page = apiPageInfo.pageNumber, pageSize = apiPageInfo.pageSize, sort = sortKey, search = searchTerm) => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        searchTerm: search,
        isActive: true,
        page,
        pageSize,
        sort,
      };

      // Check if API URL is configured
      if (!process.env.REACT_APP_API_URL) {
        throw new Error('REACT_APP_API_URL environment variable is not set. Please configure your API URL.');
      }

      const response = await getBusinessFunctions(params);

      if (response && response.data && response.data.data) {
        setBusinessFunctions(response.data.data);

        // Update pagination info from API response
        setApiPageInfo(prev => ({
          ...prev,
          totalPages: Math.max(1, response.data.totalPages || 1),
          totalCount: response.data.totalRecords || 0,
          pageNumber: page
        }));
      } else {
        setBusinessFunctions([]);
      }
    } catch (err) {
      console.error('Error fetching business functions:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        name: err.name
      });

      // Provide more specific error messages
      let errorMessage = 'Failed to fetch business functions';
      if (err.message.includes('Failed to fetch')) {
        errorMessage = 'Network error: Unable to connect to the server. Please check your internet connection and try again.';
      } else if (err.message.includes('Unauthorized')) {
        errorMessage = 'Authentication error: Please log in again.';
      } else if (err.message.includes('404')) {
        errorMessage = 'API endpoint not found. Please contact support.';
      } else if (err.message.includes('500')) {
        errorMessage = 'Server error: Please try again later.';
      } else {
        errorMessage = err.message || 'Failed to fetch business functions';
      }

      setError(errorMessage);
      setBusinessFunctions([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, apiPageInfo.pageNumber, apiPageInfo.pageSize, sortKey]);

  // Add a timeout to prevent infinite loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        console.warn('Loading timeout - resetting loading state');
        setLoading(false);
      }
    }, 30000); // 30 second timeout

    return () => clearTimeout(timeout);
  }, [loading]);



  // Load data on component mount
  useEffect(() => {
    fetchBusinessFunctions(apiPageInfo.pageNumber, apiPageInfo.pageSize);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!dataFetchedRef.current) {
      dataFetchedRef.current = true;
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      fetchBusinessFunctions(1, apiPageInfo.pageSize, sortKey, searchTerm);
    }, 300);

    return () => window.clearTimeout(timerId);
    // eslint-disable-next-line
  }, [searchTerm]);

  // Business Functions handlers
  const handleShowBusinessFunction = useCallback((businessFunction = null) => {

    if (businessFunction) {
      // Map subBusinessFunctions from API response to subFunctions for the form
      const mappedSubFunctions = businessFunction.subBusinessFunctions ?
        businessFunction.subBusinessFunctions.map(subFunc => ({
          id: subFunc.id,
          name: subFunc.name || "",
          code: subFunc.code || "",
          isActive: subFunc.isActive !== undefined ? subFunc.isActive : true
        })) : [];

      setBusinessFunctionForm({
        id: businessFunction.id,
        name: businessFunction.name || "",
        code: businessFunction.code || "",
        isActive: businessFunction.isActive !== undefined ? businessFunction.isActive : true,
        subFunctions: mappedSubFunctions
      });

    } else {
      setBusinessFunctionForm({
        id: 0,
        name: "",
        code: "",
        isActive: true,
        subFunctions: []
      });
    }
    setShowModal(true);
  }, []);



  const handleClose = useCallback(() => {
    setShowModal(false);
    // Reset form when closing modal
    setBusinessFunctionForm({
      id: 0,
      name: "",
      code: "",
      isActive: true,
      subFunctions: []
    });
  }, []);

  const handleBusinessFunctionChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setBusinessFunctionForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);



  // New handlers for sub-function fields
  const handleAddSubFunctionField = useCallback(() => {
    setBusinessFunctionForm(prev => ({
      ...prev,
      subFunctions: [...prev.subFunctions, { id: 0, name: "", code: "", isActive: true }]
    }));
  }, []);

  const handleRemoveSubFunctionField = useCallback((index) => {
    setBusinessFunctionForm(prev => {
      const newSubFunctions = [...prev.subFunctions];
      newSubFunctions.splice(index, 1);
      return { ...prev, subFunctions: newSubFunctions };
    });
  }, []);

  const handleSubFunctionFieldChange = useCallback((index, field, value) => {
    setBusinessFunctionForm(prev => ({
      ...prev,
      subFunctions: prev.subFunctions.map((subFunction, i) =>
        i === index ? { ...subFunction, [field]: value } : subFunction
      )
    }));
  }, []);



  const handleBusinessFunctionSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const { name, code, subFunctions } = businessFunctionForm;
      if (name && code) {
        try {
          setLoading(true);

          // Prepare data for API
          const businessFunctionData = {
            id: businessFunctionForm.id || 0,
            name: name.trim(),
            code: code.trim().toUpperCase(),
            isActive: businessFunctionForm.isActive,
            subBusinessFunctions: subFunctions || []
          };

          // Call API to add or update business function
          const response = await addOrUpdateBusinessFunction(businessFunctionData);

          if (response && (response.succeeded !== false || response.id !== undefined)) {
            // Get the business function ID from the response or use the form ID
            const businessFunctionId = response.id || response.data?.id || businessFunctionForm.id;

            dashboardToast.success(`Business Function ${businessFunctionForm.id === 0 ? 'created' : 'updated'} successfully`);

            // Close the modal first
            handleClose();

            // Then refresh the business functions list
            try {
              await fetchBusinessFunctions();
            } catch (refreshError) {
              console.error('Error refreshing business functions:', refreshError);
              // Don't show error to user as the main operation was successful
            }
          } else {
            dashboardToast.error(response?.message || "Failed to save business function");
          }
        } catch (err) {
          console.error('Error saving business function:', err);
          dashboardToast.error(err.message || "Failed to save business function");
        } finally {
          setLoading(false);
        }
      } else {
        dashboardToast.error("Please fill in all required fields");
      }
    },
    [businessFunctionForm, handleClose, fetchBusinessFunctions]
  );



  const handleDelete = useCallback((item, type) => {
    setItemToDelete(item);
    setDeleteType(type);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    try {
      setLoading(true);

      if (deleteType === "businessFunction") {
        // Delete business function using API
        const response = await deleteBusinessFunction(itemToDelete.id, false);

        if (response && (response.success || response.succeeded !== false)) {
          // Refresh the business functions list after successful deletion
          await fetchBusinessFunctions();

          // Sub-business functions are part of the main business function data, so they'll be removed when we refresh

          // Remove from expanded rows
          setExpandedRows(prev => {
            const newSet = new Set(prev);
            newSet.delete(itemToDelete.id);
            return newSet;
          });

          dashboardToast.success("Business Function and related Sub Business Functions deleted successfully");
        } else {
          dashboardToast.error(response?.message || "Failed to delete business function");
        }
      } else if (deleteType === "subBusinessFunction") {
        // Delete sub-business function using the same API endpoint
        const response = await deleteBusinessFunction(itemToDelete.id, true);

        if (response && (response.success || response.succeeded !== false)) {
          // Remove the deleted sub-function from the form state immediately
          setBusinessFunctionForm(prev => ({
            ...prev,
            subFunctions: prev.subFunctions.filter(subFunc => subFunc.id !== itemToDelete.id)
          }));

          // Refresh the business functions list after successful deletion
          await fetchBusinessFunctions();

          dashboardToast.success("Sub Business Function deleted successfully");
        } else {
          dashboardToast.error(response?.message || "Failed to delete sub business function");
        }
      }
    } catch (err) {
      console.error('Error deleting item:', err);
      dashboardToast.error(err.message || "Failed to delete item");
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setItemToDelete(null);
      setDeleteType("");
    }
  }, [itemToDelete, deleteType, fetchBusinessFunctions]);

  const cancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setItemToDelete(null);
    setDeleteType("");
  }, []);

  const openViewModal = useCallback((record, mode = "parent") => {
    setViewRecord(record);
    setViewMode(mode);
    setShowViewModal(true);
  }, []);

  const closeViewModal = useCallback(() => {
    setShowViewModal(false);
    setViewRecord(null);
    setViewMode("parent");
  }, []);

  const goToPage = useCallback(n => {
    const pg = Math.min(Math.max(n, 1), apiPageInfo.totalPages);
    if (pg !== apiPageInfo.pageNumber) {
      fetchBusinessFunctions(pg, apiPageInfo.pageSize);
    }
  }, [apiPageInfo.pageNumber, apiPageInfo.totalPages, apiPageInfo.pageSize, fetchBusinessFunctions]);

  const handleSortChange = (nextSort) => {
    setSortKey(nextSort);
    fetchBusinessFunctions(1, apiPageInfo.pageSize, nextSort, searchTerm);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Handle refresh
  const handleRefresh = useCallback(() => {
    fetchBusinessFunctions();
  }, [fetchBusinessFunctions]);

  // Toggle row expansion
  const toggleRowExpansion = useCallback((businessFunctionId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(businessFunctionId)) {
        newSet.delete(businessFunctionId);
      } else {
        newSet.add(businessFunctionId);
      }
      return newSet;
    });
  }, []);

  // Get sub business functions for a specific business function
  const getSubBusinessFunctionsForDisplay = (businessFunctionId) => {
    const businessFunction = businessFunctions.find(bf => bf.id === businessFunctionId);
    if (businessFunction && businessFunction.subBusinessFunctions) {
      return businessFunction.subBusinessFunctions;
    }
    return [];
  };

  // Get sub business functions count for a business function
  const getSubBusinessFunctionsCount = (businessFunctionId) => {
    const businessFunction = businessFunctions.find(bf => bf.id === businessFunctionId);
    if (businessFunction && businessFunction.subBusinessFunctions) {
      return businessFunction.subBusinessFunctions.length;
    }
    return 0;
  };

  // Get business functions (already paginated from API)
  const getPaginatedBusinessFunctions = () => {
    return businessFunctions;
  };

  return (
    <DashboardShell
      isMenuCollapsed={isMenuCollapsed}
      sidebar={
        <Menu
          onSelect={handleMenuSelection}
          selectedItem={selectedItem}
          isCollapsed={isMenuCollapsed}
          onToggleCollapse={toggleMenuCollapse}
        />
      }
    >
      <SaListPageShell topBarTitle="Business Functions Management">
        <div className="dash-frame-parent1">
          <div className="dash-list-toolbar">
            <div className="dash-list-toolbar__intro">
              <h5 className="dash-list-toolbar__title">Manage Business Functions</h5>
              <p className="dash-list-toolbar__desc">
                Create and manage business functions and related sub-functions.
              </p>
            </div>
            <div className="dash-list-toolbar__actions">
              <div className="dash-search-field dash-table-search">
                <Search className="dash-search-field__icon" aria-hidden />
                <input
                  type="text"
                  placeholder="Search by name…"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="dash-search-field__input"
                  disabled={loading}
                />
              </div>
              <TableSortSelect variant="chip"
                value={sortKey}
                onChange={handleSortChange}
                disabled={loading}
              />
              <UiButton variant="primary" className="sa-create-btn" onClick={() => handleShowBusinessFunction()}>
                Create Function
              </UiButton>
            </div>
          </div>

          {loading ? (
            <ContentLoader label="Loading business functions…" />
          ) : error ? (
            <Alert variant="error">{error}</Alert>
          ) : (
            <Fragment>
              <div className="app-table-wrap app-table-wrap--refined">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th aria-label="Expand" />
                      <th>#</th>
                      <th>Name</th>
                      <th>Code</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getPaginatedBusinessFunctions().length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center">
                          <span className="app-td__desc">No business functions found.</span>
                        </td>
                      </tr>
                    ) : (
                      getPaginatedBusinessFunctions().map((businessFunction, index) => {
                        const subFunctions = getSubBusinessFunctionsForDisplay(businessFunction.id);
                        const isExpanded = expandedRows.has(businessFunction.id);
                        const serialNumber =
                          (apiPageInfo.pageNumber - 1) * apiPageInfo.pageSize + index + 1;
                        const displayName = businessFunction.name || "Untitled function";

                        return (
                          <Fragment key={businessFunction.id}>
                            <tr
                              className="rdm-row-clickable"
                              onClick={() => openViewModal(businessFunction, "parent")}
                            >
                              <td onClick={(e) => e.stopPropagation()}>
                                <button
                                  type="button"
                                  className="vd-expand-btn"
                                  onClick={() => toggleRowExpansion(businessFunction.id)}
                                  title={
                                    isExpanded ? "Collapse sub-functions" : "Expand sub-functions"
                                  }
                                  aria-expanded={isExpanded}
                                  aria-label={
                                    isExpanded
                                      ? `Collapse ${displayName}`
                                      : `Expand ${displayName}`
                                  }
                                >
                                  {isExpanded ? <ChevronDown aria-hidden /> : <ChevronRight aria-hidden />}
                                </button>
                              </td>
                              <td>
                                <span className="app-td__index">{serialNumber}</span>
                              </td>
                              <td>
                                <span className="app-td__title">{displayName}</span>
                              </td>
                              <td>
                                {businessFunction.code ? (
                                  <span className="app-td__meta">{businessFunction.code}</span>
                                ) : (
                                  <EmptyCell />
                                )}
                              </td>
                              <td>
                                <span className="app-td__meta">Function</span>
                              </td>
                              <td>
                                <span
                                  className={
                                    businessFunction.isActive
                                      ? "app-status app-status--success"
                                      : "app-status app-status--muted"
                                  }
                                >
                                  {businessFunction.isActive ? "Active" : "Inactive"}
                                </span>
                              </td>
                              <td
                                className="dash-mobile-table-actions"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="btn-list-actions">
                                  <UiButton
                                    variant="outline"
                                    size="sm"
                                    className="btn-list-edit"
                                    title="Edit"
                                    aria-label={`Edit ${displayName}`}
                                    onClick={() => handleShowBusinessFunction(businessFunction)}
                                  >
                                    <Pencil />
                                  </UiButton>
                                  <UiButton
                                    variant="danger"
                                    size="sm"
                                    className="btn-list-delete"
                                    title="Delete"
                                    aria-label={`Delete ${displayName}`}
                                    onClick={() =>
                                      handleDelete(businessFunction, "businessFunction")
                                    }
                                  >
                                    <Trash2 />
                                  </UiButton>
                                </div>
                              </td>
                            </tr>

                            {isExpanded ? (
                              subFunctions.length > 0 ? (
                                subFunctions.map((subFunction) => (
                                  <tr
                                    key={`sub-${subFunction.id}`}
                                    className="rdm-row-clickable vd-sub-row"
                                    onClick={() => openViewModal(subFunction, "child")}
                                  >
                                    <td />
                                    <td />
                                    <td>
                                      <span className="app-td__desc vd-sub-name">
                                        {subFunction.name}
                                      </span>
                                    </td>
                                    <td>
                                      {subFunction.code ? (
                                        <span className="app-td__meta">{subFunction.code}</span>
                                      ) : (
                                        <EmptyCell />
                                      )}
                                    </td>
                                    <td>
                                      <span className="app-td__meta">Sub-function</span>
                                    </td>
                                    <td>
                                      <span
                                        className={
                                          subFunction.isActive
                                            ? "app-status app-status--success"
                                            : "app-status app-status--muted"
                                        }
                                      >
                                        {subFunction.isActive ? "Active" : "Inactive"}
                                      </span>
                                    </td>
                                    <td>
                                      <EmptyCell />
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr className="vd-sub-row">
                                  <td colSpan={7} className="text-center">
                                    <span className="app-td__desc">
                                      No sub-functions found for this business function.
                                    </span>
                                  </td>
                                </tr>
                              )
                            ) : null}
                          </Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <UiPagination
                page={apiPageInfo.pageNumber}
                totalPages={apiPageInfo.totalPages}
                onPageChange={goToPage}
              />
            </Fragment>
          )}
        </div>
      </SaListPageShell>

      {/* Create/Edit Modal */}
      <Modal
        show={showModal}
        onHide={handleClose}
        centered
        size="lg"
        dialogClassName="rounded-3"
      >
        <Modal.Header closeButton className="dashboard-modal-header">
          {businessFunctionForm.id > 0 ? (
            <>
              <Settings className="h-5 w-5 me-2" />
              <Modal.Title>Edit Business Function</Modal.Title>
            </>
          ) : (
            <>
              <Settings className="h-5 w-5 me-2" />
              <Modal.Title>Create Business Function</Modal.Title>
            </>
          )}
        </Modal.Header>

        <Form onSubmit={handleBusinessFunctionSubmit}>
          <Modal.Body className="px-4">
            <Row>
              <Col md={6}>
                <Form.Group controlId="formName" className="mb-3">
                  <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={businessFunctionForm.name}
                    onChange={handleBusinessFunctionChange}
                    required
                    placeholder="Enter business function name"
                    maxLength="100"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formCode" className="mb-3">
                  <Form.Label>Code <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="code"
                    value={businessFunctionForm.code}
                    onChange={handleBusinessFunctionChange}
                    required
                    placeholder="Enter business function code"
                    maxLength="10"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group controlId="formIsActive" className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <div className="mt-2">
                    <Form.Check
                      type="checkbox"
                      name="isActive"
                      checked={businessFunctionForm.isActive}
                      onChange={handleBusinessFunctionChange}
                      label="Active"
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>

            {/* Sub Function Management Section */}
            <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
              <h6 className="mb-0">
                <Layers className="me-2" />
                Sub Functions
              </h6>
              <Button
                onClick={() => handleAddSubFunctionField()}
                className="btn btn-dashboard-primary"
                size="sm"
              >
                <Plus className="me-1" />
                Add New Sub Function
              </Button>
            </div>

            {/* Sub Function Fields */}
            {businessFunctionForm.subFunctions && businessFunctionForm.subFunctions.length > 0 ? (
              businessFunctionForm.subFunctions.map((subFunction, index) => (
                <div key={subFunction.id || `new-${index}`} className="border rounded p-2 mb-2 dashboard-form-section">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">
                      {subFunction.id > 0 ? `Sub Function` : `New Sub Function`}
                      {subFunction.id > 0}
                    </h6>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="cap-mgmt-icon-btn"
                      onClick={() => {
                        if (subFunction.id > 0) {
                          // If it's an existing sub-function, show delete confirmation
                          handleDelete(subFunction, "subBusinessFunction");
                        } else {
                          // If it's a new sub-function, just remove from form
                          handleRemoveSubFunctionField(index);
                        }
                      }}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                  <Row>
                    <Col md={4}>
                      <Form.Group controlId={`subFunctionName${index}`} className="mb-2">
                        <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          value={subFunction.name}
                          onChange={(e) => handleSubFunctionFieldChange(index, 'name', e.target.value)}
                          placeholder="Enter sub function name"
                          maxLength="100"
                          style={{ height: '38px' }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group controlId={`subFunctionCode${index}`} className="mb-2">
                        <Form.Label>Code <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          value={subFunction.code}
                          onChange={(e) => handleSubFunctionFieldChange(index, 'code', e.target.value)}
                          placeholder="Enter sub function code"
                          maxLength="10"
                          style={{ height: '38px' }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group controlId={`subFunctionStatus${index}`} className="mb-2">
                        <Form.Label>Status</Form.Label>
                        <Form.Select
                          value={subFunction.isActive ? 'true' : 'false'}
                          onChange={(e) => handleSubFunctionFieldChange(index, 'isActive', e.target.value === 'true')}
                          style={{ height: '38px' }}
                        >
                          <option value="true">Active</option>
                          <option value="false">Inactive</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-2">
                        <Form.Label>&nbsp;</Form.Label>
                        <div className="d-flex align-items-center" style={{ height: '38px' }}>
                          <span className={`badge ${subFunction.isActive ? 'bg-success' : 'bg-secondary'}`}>
                            {subFunction.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              ))
            ) : null}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="btn btn-dashboard-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Business Function'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal show={showDeleteModal} onHide={cancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete{" "}
          <strong>{itemToDelete?.name}</strong>?
          {deleteType === "businessFunction" && (
            <div className="alert alert-warning mt-2">
              <strong>Warning:</strong> This will also delete all related Sub Business Functions.
            </div>
          )}
          {deleteType === "subBusinessFunction" && (
            <div className="alert alert-info mt-2">
              <strong>Info:</strong> This will delete the Sub Business Function.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelDelete} disabled={loading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={loading}>
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>

      <RecordDetailModal
        show={showViewModal}
        onHide={closeViewModal}
        title={
          viewRecord?.name ||
          (viewMode === "child" ? "Sub Function Details" : "Business Function Details")
        }
        subtitle={
          viewMode === "child" ? "Sub function record" : "Business function record"
        }
        fields={
          viewMode === "child"
            ? [
                { label: "Name", value: viewRecord?.name },
                { label: "Code", value: viewRecord?.code },
                { label: "Type", value: "Sub Function" },
                { label: "Active", value: Boolean(viewRecord?.isActive) },
              ]
            : [
                { label: "Name", value: viewRecord?.name },
                { label: "Code", value: viewRecord?.code },
                { label: "Type", value: "Business Function" },
                { label: "Active", value: Boolean(viewRecord?.isActive) },
              ]
        }
        groups={
          viewMode === "parent"
            ? [
                {
                  title: "Sub Functions",
                  content: (() => {
                    const children = viewRecord
                      ? getSubBusinessFunctionsForDisplay(viewRecord.id)
                      : [];
                    if (!children.length) {
                      return <span className="rdm-empty">None</span>;
                    }
                    return children.map((sub) => (
                      <div key={sub.id} className="rdm-nested-card">
                        <div className="rdm-nested-card-title">{sub.name}</div>
                        <div className="rdm-nested-meta">Code: {sub.code || "—"}</div>
                        <div className="rdm-nested-meta">
                          {sub.isActive ? "Active" : "Inactive"}
                        </div>
                      </div>
                    ));
                  })(),
                },
              ]
            : []
        }
        actions={
          viewMode === "parent"
            ? [
                {
                  type: "edit",
                  onClick: () => {
                    const record = viewRecord;
                    closeViewModal();
                    if (record) handleShowBusinessFunction(record);
                  },
                },
                {
                  type: "delete",
                  onClick: () => {
                    const record = viewRecord;
                    closeViewModal();
                    if (record) handleDelete(record, "businessFunction");
                  },
                },
              ]
            : []
        }
      />

    </DashboardShell>
  );
};

export default FunctionsManagement;
